import { getOrderValueFromPrice } from '@/features/trade/order/utils/math';
import { useAccountStore } from '@/store/use-account-store';
import {
  getAllMarketStoreStates,
  getAllSymbols,
  useMarketStore,
} from '@/store/use-markets-store';
import { useOrdersStore } from '@/store/use-orders-store';
import { OrderStatus } from '@/types';
import { abbrFromSym } from '@/utils/token-symbol';
import {
  bigIntToDecimalStr,
  bigIntToNum,
  formatNumber,
} from '@/utils/value-format';
import { useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';

type Props = {
  orderId: string;
};

export default function Value({ orderId }: Props) {
  const address = useAccountStore((state) => state.address);
  const { symbol, size, value } = useOrdersStore(
    useShallow((state) => {
      const order = state.getOrders(address).orders[orderId];

      const symbols = getAllSymbols();
      if (!symbols.includes(order.symbol)) {
        return { symbol: undefined, size: undefined, value: undefined };
      }

      let price: bigint;
      let size: bigint;

      if (
        order.status === OrderStatus.FILLED ||
        order.status === OrderStatus.PARTIALLY_FILLED
      ) {
        price = order.lastFilledPrice;
        size = order.lastFilledSize;
      } else {
        price = order.limitPrice;
        size = order.size;
      }

      return {
        symbol: order.symbol,
        size,
        value: getOrderValueFromPrice(order.symbol, size, price)?.decimal,
      };
    }),
  );

  // NOTE: in case symbol is not listed
  const sizeDecimals = useMemo(() => {
    const markets = getAllMarketStoreStates();
    if (!symbol || !(symbol in markets)) return;
    return markets[symbol].marketSpec.sizeDecimals;
  }, [symbol]);

  return (
    <div>
      <div>
        {formatNumber(value, { digits: 2, round: 'ceil', style: 'currency' })}
      </div>
      <div className="text-base text-muted-foreground">
        {sizeDecimals !== undefined && size !== undefined
          ? `${bigIntToDecimalStr(size, sizeDecimals)} ${abbrFromSym(symbol)}`
          : '-'}
      </div>
    </div>
  );
}

export const valueAccessorFn = (id: string) => {
  const { address } = useAccountStore.getState();
  const order = useOrdersStore.getState().getOrders(address).orders[id];

  const symbols = getAllSymbols();
  if (!symbols.includes(order.symbol)) return;

  let price: bigint;
  let size: bigint;

  if (
    order.status === OrderStatus.FILLED ||
    order.status === OrderStatus.PARTIALLY_FILLED
  ) {
    price = order.lastFilledPrice;
    size = order.lastFilledSize;
  } else {
    price = order.limitPrice;
    size = order.size;
  }

  const value = getOrderValueFromPrice(order.symbol, size, price);

  if (!value) return;

  const { marketSpec } = useMarketStore(order.symbol).getState();

  return bigIntToNum(
    value.bigint,
    marketSpec.sizeDecimals + marketSpec.priceDecimals,
  );
};
